import { TWidget } from 'widgets/Widget';
import { scrollWindowTo } from 'widgets/toolbox/scroll';
import { isSmallView } from 'widgets/toolbox/viewtype';
import { TPersonalizationDrawerInstance } from './PersonalizationDrawer';

export type TPersonalizationConfig = {
    optionId: string;
    customValueId: string;
    defaultValueId: string;
    maxLength: number;
    pattern: string;
    blockedWords: Array<string>;
    isFinalSale: boolean;
    bopisEnabled: boolean;
};

/**
 * @description PersonalizationSummary implementation
 * @param Widget Base widget for extending
 * @returns Personalization summary class
 */
function PersonalizationSummaryClassCreator(Widget: TWidget) {
    class PersonalizationSummary extends Widget {
        text = '';

        prefs() {
            return {
                optionId: '',
                customValueId: '',
                defaultValueId: '',
                price: '',
                maxLength: 3,
                pattern: '',
                blockedWords: <Array<string>>[],
                isFinalSale: false,
                bopisEnabled: false,
                drawerId: 'personalizationDrawer',
                uuid: '',
                pliuuid: '',
                customOptionValue: '',
                isCartContext: false,
                ...super.prefs()
            };
        }

        init(): void {
            super.init();

            const savedText = String(this.ref('self').attr('data-saved-text') || '');

            if (this.prefs().isCartContext) {
                this.text = savedText;

                return;
            }

            if (savedText) {
                this.setText(savedText).catch(() => undefined);
            }
        }

        /**
         * @description Get the personalization configuration of the product
         * @returns personalization configuration
         */
        getConfig(): TPersonalizationConfig {
            const prefs = this.prefs();

            return {
                optionId: prefs.optionId,
                customValueId: prefs.customValueId,
                defaultValueId: prefs.defaultValueId,
                maxLength: prefs.maxLength,
                pattern: prefs.pattern,
                blockedWords: prefs.blockedWords,
                isFinalSale: prefs.isFinalSale,
                bopisEnabled: prefs.bopisEnabled
            };
        }

        /**
         * @description Open the drawer with the active personalization
         */
        async openDrawer(): Promise<void> {
            const drawer = this.getById<TPersonalizationDrawerInstance, TPersonalizationDrawerInstance>(
                this.prefs().drawerId,
                drawerWidget => drawerWidget
            );

            await drawer?.openDrawer(this.text, this.getConfig());
        }

        /**
         * @description Update the drawer images, e.g. when another color is selected
         * @param images - images of the `personalization` view type
         */
        setImages(images: Array<Record<string, unknown>>): void {
            this.getById<TPersonalizationDrawerInstance>(this.prefs().drawerId, drawer => {
                drawer.setImages(images);
            });
        }

        /**
         * @description Drawer save handler
         * @param _drawer - drawer widget
         * @param text - valid personalization text
         */
        async savePersonalization(_drawer: TPersonalizationDrawerInstance, text: string): Promise<void> {
            if (this.prefs().isCartContext) {
                const {
                    uuid,
                    optionId
                } = this.prefs();

                const config = {
                    uuid,
                    optionId,
                    text
                };

                const url = this.ref('editButton').data<string>('editOptionUrl');

                if (!url) {
                    return;
                }

                this.editPersonalization(config, url);

                return;
            }

            await this.setText(text, true);

            const isMobile = isSmallView();

            this.ref('editButton').focus(isMobile);

            if (isMobile) {
                scrollWindowTo(this.ref('self').get(), true, true);
            }
        }

        // Replaced by the cart manager handler through data-forward-to-parent.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        editPersonalization(config: { uuid: string; optionId: string; text: string }, url: string): void {
            // replaced with the cart manager handler by the widgets manager
        }

        /**
         * @description Remove the personalization, the product goes back to the standard (not personalized) state
         */
        async removePersonalization(): Promise<void> {
            await this.setText('');

            this.ref('addButton').focus();
        }

        /**
         * @description Reset the personalization without moving the focus, e.g. when the selected product is not personalizable
         */
        reset(): void {
            if (this.text) {
                this.setText('').catch(() => undefined);
            }
        }

        /**
         * @description Set the active personalization, re-render the row and notify the product detail widget
         * @param text - personalization text, empty to remove the personalization
         * @param [showSavedMessage] - show the "successfully saved" message
         * @returns rendering promise
         */
        setText(text: string, showSavedMessage = false): Promise<void> {
            this.text = text;
            this.onPersonalizationChange(text);

            return this.render(
                'template',
                {
                    text: text,
                    price: this.prefs().price,
                    showSavedMessage: showSavedMessage
                },
                this.ref('container')
            );
        }

        /**
         * @description Handled by the product detail widget, see `data-forward-to-parent`
         * @param text - personalization text
         */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onPersonalizationChange(text: string): void {
            // replaced with the product detail widget handler by the widgets manager
        }
    }

    return PersonalizationSummary;
}

export type TPersonalizationSummary = ReturnType<typeof PersonalizationSummaryClassCreator>;

export type TPersonalizationSummaryInstance = InstanceType<TPersonalizationSummary>;

export default PersonalizationSummaryClassCreator;
